import { BILLING_INTERVAL } from "@/data/constants";
import useAppQueryParam from "@/hooks/useAppQueryParam";
import { useIsLocalisedPricing } from "@/hooks/useIsLocalisedPricing";
import { useSearchParams } from "@/lib/router";
import { useViewTracking } from "@/tracking/useViewTracking";
import { TBillingInterval } from "@/types/app";
import { parsePromoCode } from "@/utils/parseQuery";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "reactfire";
import {
  LEDGER_MIN_VISIBLE_MS,
  SWITCH_PLAN_MODAL_PARAM,
} from "../constants.dashboard";
import { DASHBOARD_EVENTS } from "../events.dashboard";
import type { EstimateIntervalData } from "@/components/shared/interval-switch/estimate-interval-change";
import { DashboardService } from "../service.dashboard";
import type { TLedgerAmounts, TYearlyUpsaleLedger } from "../types.dashboard";
import { getAppliedPromoCode } from "../utils/getAppliedPromoCode";
import { toSwitchTodayAmounts } from "../utils/toSwitchTodayAmounts";
import { useEstimateIntervalChangeQuery } from "@/components/shared/interval-switch/use-estimate-interval-change-query";
import { useConfirmIntervalSwitch } from "./useConfirmIntervalSwitch";

const COPY = DashboardService.Content.yearlyPlanSaleLedger;

// A stable fallback, so the promo field's memos keyed on discounts hold
const NO_DISCOUNTS: EstimateIntervalData["discounts"] = [];

interface LedgerSwitchOptions {
  amounts: TLedgerAmounts;
  /** The estimate the amounts were built from */
  estimate: EstimateIntervalData;
  /** Keys the confirm-time estimate like the ledger's, so they share a cache entry */
  planCurrency: string;
  billingPeriod: TBillingInterval;
  /** One per owned plan */
  planNames: string[];
  isV1?: boolean;
}

/**
 * Wires the ledger's switch flow: the URL-driven confirm dialog, its promo
 * code and estimate, the interval switch and the tracking.
 */
export function useLedgerSwitch({
  amounts,
  estimate,
  planCurrency,
  billingPeriod,
  planNames,
  isV1,
}: LedgerSwitchOptions): TYearlyUpsaleLedger {
  const auth = useAuth();
  const { isLocalisedPricing } = useIsLocalisedPricing();

  // The confirm dialog shares the banner's URL state, so campaign links on
  // ?upgrade-interval=true open whichever variant the user gets
  const { getQueryParam, addQueryParam, removeQueryParam } = useAppQueryParam();
  const [searchParams] = useSearchParams();
  const isConfirmOpen = getQueryParam(SWITCH_PLAN_MODAL_PARAM) === "true";

  // Prefill the promo code from the URL: ?promo= | ?promo_code= | ?coupon= | ?discount=
  const queryPromoCode = parsePromoCode(searchParams) ?? "";
  const [promoCode, setPromoCode] = useState(queryPromoCode);

  // The ledger's estimate may be stale by the time the user confirms, so the
  // dialog fetches its own on open; without a promo code it shares the cache
  // entry, with one it leaves the ledger's eligibility untouched
  const { data: switchEstimate, isFetching: isEstimatingSwitch } =
    useEstimateIntervalChangeQuery({
      targetInterval: BILLING_INTERVAL.YEARLY,
      promoCode,
      enabled: isConfirmOpen,
      isV1,
      isLocalisedPricing,
      currency: planCurrency,
    });

  // Success refetches the user plan, which takes yearly users off the upsell
  const { confirmSwitch, isPending: isSwitching } = useConfirmIntervalSwitch({
    track: DashboardService.track,
    isV1,
    onSuccess: () => toast.success(COPY.switchToday.successToast),
  });

  const ledgerEventProps = {
    user_id: auth.currentUser?.uid,
    plan_names: planNames,
    current_billing_period: billingPeriod,
    currency: amounts.currency,
    monthly_price: amounts.monthlyPrice,
    // months on the current plan: its unbroken run of paid monthly renewals
    months_paid: amounts.monthsPaid,
    yearly_price: amounts.yearlyPrice,
    amount_due: amounts.amountDueToday,
    plan_count: planNames.length,
  };

  const viewRef = useRef<HTMLElement>(null);
  useViewTracking(viewRef, {
    track: DashboardService.track,
    event: DASHBOARD_EVENTS.YEARLY_LEDGER_VIEWED,
    properties: ledgerEventProps,
    minVisibleMs: LEDGER_MIN_VISIBLE_MS,
  });

  const entryPointRef = useRef<"ledger_click" | "deep_link">("deep_link");

  // Measured on open rather than on click, so deep-link opens count too
  useEffect(() => {
    if (!isConfirmOpen) {
      entryPointRef.current = "deep_link";
      return;
    }

    DashboardService.track(DASHBOARD_EVENTS.INTERVAL_SWITCH_MODAL_VIEWED, {
      ...ledgerEventProps,
      entry_point: entryPointRef.current,
      // the code the campaign link carried, before the user edits it
      promo_code: queryPromoCode || undefined,
    });
    // Keyed on open state alone, as in the banner: re-firing whenever the
    // amounts re-derive would inflate the step
  }, [isConfirmOpen]);

  // Until the dialog's estimate lands, show the one the ledger was built from
  const confirmEstimate = switchEstimate ?? estimate;
  const appliedPromoCode = getAppliedPromoCode(switchEstimate?.discounts);

  function handleSwitchClick() {
    entryPointRef.current = "ledger_click";
    DashboardService.track(
      DASHBOARD_EVENTS.YEARLY_LEDGER_CLICKED,
      ledgerEventProps,
    );
  }

  // Rejects on failure, which keeps the confirm dialog open
  async function handleSwitchToYearly() {
    DashboardService.track(
      DASHBOARD_EVENTS.YEARLY_LEDGER_CONFIRM_CLICKED,
      ledgerEventProps,
    );
    await confirmSwitch({
      event: DASHBOARD_EVENTS.YEARLY_LEDGER_SWITCH_CONFIRMED,
      targetInterval: BILLING_INTERVAL.YEARLY,
      amountDueMinor: confirmEstimate.amount_due,
      currency: confirmEstimate.currency ?? planCurrency,
      promoCode: appliedPromoCode,
      properties: ledgerEventProps,
    });
  }

  function handleConfirmOpenChange(open: boolean) {
    // each open starts from the URL-provided code (empty when there is none)
    setPromoCode(queryPromoCode);
    if (open) {
      addQueryParam(SWITCH_PLAN_MODAL_PARAM, "true");
      return;
    }
    removeQueryParam(SWITCH_PLAN_MODAL_PARAM);
  }

  function handleApplyPromo(code: string) {
    setPromoCode(code);
    DashboardService.track(DASHBOARD_EVENTS.INTERVAL_SWITCH_PROMO_APPLIED, {
      promo_code: code,
      target_interval: BILLING_INTERVAL.YEARLY,
    });
  }

  function handleRemovePromo(code: string) {
    setPromoCode("");
    DashboardService.track(DASHBOARD_EVENTS.INTERVAL_SWITCH_PROMO_REMOVED, {
      promo_code: code,
      target_interval: BILLING_INTERVAL.YEARLY,
    });
  }

  return {
    ...amounts,
    ...toSwitchTodayAmounts(confirmEstimate),
    viewRef,
    onSwitchClick: handleSwitchClick,
    onSwitchToYearly: handleSwitchToYearly,
    isConfirmOpen,
    onConfirmOpenChange: handleConfirmOpenChange,
    promo: {
      discounts: switchEstimate?.discounts ?? NO_DISCOUNTS,
      onApply: handleApplyPromo,
      onRemove: handleRemovePromo,
      isApplying: isEstimatingSwitch,
      isDisabled: isSwitching,
    },
    isEstimating: isEstimatingSwitch,
  };
}
